import { acquireGalaxyChat } from '../../utils/galaxyChatParticipation';
import './galaxyRightbarMobile.css';
import React, {
  useEffect,
  useState,
  useContext,
  useRef,
  useCallback,
  useMemo,
  memo,
} from 'react';
import axiosConn from '../../axiosConn';
import { AuthContext } from '../../context/AuthContext';
import { SocketContext } from '../../context/socket';
import { Link } from 'react-router-dom';
import arePropsEqual from '../../arePropsEqual';
import {
  isPlayerAuthorized,
} from '../../utils/galaxyUtils';
import { useTheme } from '../../context/ThemeContext';
import { usePresence } from '../../context/PresenceContext';
import useGalaxySubscriberPresence from '../../hooks/useGalaxySubscriberPresence';
import useGalaxyMemberAdministration from '../../hooks/useGalaxyMemberAdministration';
import GalaxyMemberRestoration from '../galaxyRightbar/GalaxyMemberRestoration';

const AVAILABILITY_LABELS = {
  online: 'Online',
  idle: 'Idle',
  recent: 'Recently online',
};

export default memo(
  function GalaxyRightBarMobile({
    galaxy: initialGalaxy,
    onJgkTokenSumChange,
    parent = '',
    galaxyChatOnlinePlayers,
    hydratedMembers,
    onMembersChanged,
  }) {
    const galaxy = useMemo(() => initialGalaxy, [initialGalaxy]);

    const hasHydratedMembers = Array.isArray(hydratedMembers);
    const [subscribers, setSubscribers] = useState(
      hasHydratedMembers ? hydratedMembers : [],
    );
    const { player } = useContext(AuthContext);
    const playerIsAuthorized = isPlayerAuthorized(galaxy, player);
    const administration = useGalaxyMemberAdministration({ galaxy, player, subscribers, setSubscribers, onMembersChanged });

    const { socket } = useContext(SocketContext);
    // const totalTranslation = subscribers ? subscribers.length * 8 : 0;
    const prevSubscribersRef = useRef(); // Create a ref to store the previous subscribers
    const { darkTheme } = useTheme();
    const { presenceMap } = usePresence() || {};

    const [galaxyChats, setGalaxyChats] = useState({});

    useEffect(() => {
      if (parent === 'galaxyPage') {
        socket.emit('fetchGalaxyChat', galaxy._id);
      }
    }, [parent, socket, galaxy._id]);

    const handleGetGalaxyChat = useCallback((galaxyChat) => {
      setGalaxyChats(galaxyChat);
    }, []);

    useEffect(() => {
      socket.on('getGalaxyChat', handleGetGalaxyChat);

      const release = parent === 'galaxyPage'
        ? acquireGalaxyChat({ socket, galaxyId: galaxy._id, playerId: player.data.player._id, token: player.token })
        : () => {};
      if (parent === 'galaxyPage') socket.emit('fetchGalaxyChat', galaxy._id);

      return () => {
        socket.off('getGalaxyChat', handleGetGalaxyChat);
        release();
      };
    }, [
      socket,
      galaxy._id,
      parent,
      player.data.player._id,
      player.token,
      handleGetGalaxyChat,
    ]);

    const activeGalaxyChatPlayers = useMemo(() => {
      if (parent === 'galaxyPage') return galaxyChats?.players || [];
      return galaxyChatOnlinePlayers || [];
    }, [galaxyChatOnlinePlayers, galaxyChats?.players, parent]);

    const subscriberPresence = useGalaxySubscriberPresence({
      subscribers,
      presenceMap,
      galaxyChatOnlinePlayers: activeGalaxyChatPlayers,
    });

    useEffect(() => {
      if (!hasHydratedMembers) return;
      setSubscribers(hydratedMembers);
      prevSubscribersRef.current = hydratedMembers;
    }, [hasHydratedMembers, hydratedMembers]);

    useEffect(() => {
      if (!hasHydratedMembers) return;
      const jgkTokenSum = subscribers.reduce(
        (sum, member) => sum + (Number(member.jgkTokens) || 0),
        0,
      );
      onJgkTokenSumChange(jgkTokenSum);
    }, [hasHydratedMembers, subscribers, onJgkTokenSumChange]);

    useEffect(() => {
      if (parent === 'galaxyPage' || hasHydratedMembers) return undefined;
      let isMounted = true;

      const fetchSubscribers = async () => {
        try {
          const response = await axiosConn.get(
            `galaxies/${galaxy._id}/members`,
            {
              headers: {
                Authorization: `Bearer ${player.token}`,
              },
            },
          );
          if (!isMounted) return;

          if (
            JSON.stringify(response.data.members) !==
            JSON.stringify(prevSubscribersRef.current)
          ) {
            setSubscribers(response.data.members);
            prevSubscribersRef.current = response.data.members;

            const jgkTokenSum = response.data.members.reduce(
              (sum, member) => sum + (Number(member.jgkTokens) || 0),
              0,
            );
            onJgkTokenSumChange(jgkTokenSum);
          }
        } catch (error) {
          console.error('Failed to fetch subscribers: ', error);
        }
      };

      fetchSubscribers();

      return () => {
        isMounted = false;
      };
    }, [
      galaxy._id,
      player.token,
      onJgkTokenSumChange,
      parent,
      hasHydratedMembers,
    ]);

    // if (parent === 'galaxyPage') console.log('galaxyChats: ', galaxyChats);
    // Helper function

    return (
      <>
        {subscribers.some(
          (subscriber) => subscriber._id === player.data.player._id,
        ) && (
          <div className="feedFriendCount">
            {subscribers && subscribers.length === 0
              ? ''
              : subscribers.length === 1
              ? '1 Galaxy Subscriber'
              : `${subscribers.length} Galaxy Subscribers`}
          </div>
        )}
        <GalaxyMemberRestoration administration={administration} />
        <div className="centerWrapper">
          {(playerIsAuthorized || subscribers.some(
            (subscriber) => subscriber._id === player.data.player._id,
          )) && (
            <div className="feedFriendImgs">
              {subscribers &&
                [...subscribers]
                  .sort((a, b) => b.jgkTokens - a.jgkTokens)
                  .map((subscriber, i) => {
                    const subscriberKey =
                      subscriber._id ||
                      subscriber.playerId ||
                      subscriber.accountId ||
                      subscriber.id;
                    const presenceState =
                      subscriberPresence[subscriberKey] || {};
                    const isInGalaxy = Boolean(presenceState.isInGalaxy);
                    const isPresenceVisible = Boolean(
                      presenceState.isPresenceVisible,
                    );
                    const availabilityStatus =
                      presenceState.availabilityStatus || 'none';
                    const availabilityLabel =
                      AVAILABILITY_LABELS[availabilityStatus];

                    if (parent === 'galaxyPage' && !isInGalaxy) {
                      return null;
                    }

                    return (
                      <div key={subscriberKey} className="playerWrapper">
                        <Link
                          to={`/profile/${subscriber.playerId}`}
                          className="linkWrapper"
                          style={{
                            zIndex: `${i}`,
                            margin: '1px',
                          }}
                        >
                          <div className="subscriberWrapper">
                            <img
                              className="subscriberPhoto"
                              src={
                                subscriber.profilePhoto
                                  ? subscriber.profilePhoto
                                  : 'https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1656194537/Images/noAvatar_tsgpm9.png'
                              }
                              alt={subscriber.playerId}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src =
                                  'https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1656194537/Images/noAvatar_tsgpm9.png';
                              }}
                            />
                            {isPresenceVisible && (
                              <span
                                aria-label={availabilityLabel}
                                className={`galaxySubscriberPresenceDot galaxySubscriberPresenceDot--${availabilityStatus}`}
                                role="img"
                                title={availabilityLabel}
                              />
                            )}

                            {isInGalaxy && (
                              <span
                                aria-label="In this Galaxy"
                                className="galaxySubscriberPresenceDot galaxySubscriberPresenceDot--galaxy"
                                role="img"
                                title="In this Galaxy"
                              />
                            )}
                          </div>
                        </Link>

                        {playerIsAuthorized && (
                          <button
                            className={
                              darkTheme
                                ? 'xButtonGalaxyPlayerDark'
                                : 'xButtonGalaxyPlayer'
                            }
                            onClick={() => administration.remove(subscriber)}
                            disabled={administration.busy}
                            title={`Remove ${subscriber.playerId}`}
                          >
                            x
                          </button>
                        )}
                      </div>
                    );
                  })}
            </div>
          )}
        </div>
      </>
    );
  },
  (prevProps, nextProps) =>
    prevProps.galaxy._id === nextProps.galaxy._id &&
    prevProps.galaxy.updatedAt === nextProps.galaxy.updatedAt &&
    prevProps.parent === nextProps.parent &&
    prevProps.hydratedMembers === nextProps.hydratedMembers &&
    prevProps.galaxyChatOnlinePlayers.length ===
      nextProps.galaxyChatOnlinePlayers.length &&
    prevProps.galaxyChatOnlinePlayers.every(
      (p, i) => p.id === nextProps.galaxyChatOnlinePlayers[i]?.id,
    ),
);
